import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/config/api";
import { useAuth } from "@/contexts/AuthContext";
import { showError, showSuccess } from "@/utils/dialog";
import { TablePagination } from "@/components/TablePagination";

const STATUS_OPTIONS = ["open", "in_progress", "done", "blocked", "cancelled"];
const PRIORITY_OPTIONS = ["low", "medium", "high", "urgent"];
const ROLE_OPTIONS = ["sales", "finance", "management", "admin"];
const SOURCE_TYPE_OPTIONS = ["manual", "order", "invoice"];

export default function WorkflowTickets() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const role = String(user?.role || "").toLowerCase();
  const canUseAllView = role === "admin" || role === "management";
  const savedView = localStorage.getItem("wt_view");
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState(
    savedView || (canUseAllView ? "all" : "inbox"),
  );
  const [status, setStatus] = useState(localStorage.getItem("wt_status") || "");
  const [priority, setPriority] = useState(localStorage.getItem("wt_priority") || "");
  const [sourceType, setSourceType] = useState(localStorage.getItem("wt_sourceType") || "");
  const [toRoleFilter, setToRoleFilter] = useState(localStorage.getItem("wt_toRole") || "");
  const [assigneeFilter, setAssigneeFilter] = useState(localStorage.getItem("wt_assignee") || "");
  const [dateFrom, setDateFrom] = useState(localStorage.getItem("wt_dateFrom") || "");
  const [dateTo, setDateTo] = useState(localStorage.getItem("wt_dateTo") || "");
  const [q, setQ] = useState(localStorage.getItem("wt_q") || "");
  const [displayMode, setDisplayMode] = useState(localStorage.getItem("wt_displayMode") || "cards");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [completeModal, setCompleteModal] = useState({ open: false, ticket: null });
  const [completionRemark, setCompletionRemark] = useState("");
  const [actionLoadingId, setActionLoadingId] = useState("");
  const [detailsModal, setDetailsModal] = useState({ open: false, ticket: null });
  const [ticketEvents, setTicketEvents] = useState([]);
  const [threadComment, setThreadComment] = useState("");
  const [threadSaving, setThreadSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [adminSummary, setAdminSummary] = useState(null);
  const [form, setForm] = useState({
    title: "",
    description: "",
    priority: "medium",
    to_role: "finance",
    to_user_id: "",
    source_type: "manual",
    source_id: "",
    due_date: "",
  });

  const canCreate = useMemo(
    () => ["admin", "sales", "finance", "management"].includes(String(user?.role || "").toLowerCase()),
    [user?.role],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.workflowTickets.list({
        view,
        status,
        q,
        source_type: sourceType || undefined,
        to_role: toRoleFilter || undefined,
        to_user_id: assigneeFilter || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });
      setTickets(Array.isArray(data) ? data : []);
      const role = String(user?.role || "").toLowerCase();
      if (role === "admin" || role === "management") {
        const summary = await api.workflowTickets.summaryAdmin().catch(() => null);
        setAdminSummary(summary);
      }
    } catch (e) {
      await showError(e.message || "Failed to load workflow tickets");
    } finally {
      setLoading(false);
    }
  }, [view, status, q, sourceType, toRoleFilter, assigneeFilter, dateFrom, dateTo, user?.role]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    localStorage.setItem("wt_view", view);
    localStorage.setItem("wt_status", status);
    localStorage.setItem("wt_priority", priority);
    localStorage.setItem("wt_sourceType", sourceType);
    localStorage.setItem("wt_toRole", toRoleFilter);
    localStorage.setItem("wt_assignee", assigneeFilter);
    localStorage.setItem("wt_dateFrom", dateFrom);
    localStorage.setItem("wt_dateTo", dateTo);
    localStorage.setItem("wt_q", q);
    localStorage.setItem("wt_displayMode", displayMode);
  }, [view, status, priority, sourceType, toRoleFilter, assigneeFilter, dateFrom, dateTo, q, displayMode]);

  useEffect(() => {
    setPage(1);
  }, [view, status, priority, sourceType, toRoleFilter, assigneeFilter, dateFrom, dateTo, q, displayMode]);

  useEffect(() => {
    if (String(user?.role || "").toLowerCase() === "admin") {
      api.users.getAll().then(setUsers).catch(() => setUsers([]));
    }
  }, [user?.role]);

  useEffect(() => {
    const sourceType = String(searchParams.get("source_type") || "").trim();
    const sourceId = String(searchParams.get("source_id") || "").trim();
    const title = String(searchParams.get("title") || "").trim();
    const description = String(searchParams.get("description") || "").trim();
    const toRole = String(searchParams.get("to_role") || "").trim().toLowerCase();
    const dueDate = String(searchParams.get("due_date") || "").trim();
    if (!sourceType && !sourceId && !title && !description && !toRole && !dueDate) return;
    setForm((prev) => ({
      ...prev,
      source_type: sourceType || prev.source_type,
      source_id: sourceId || prev.source_id,
      title: title || prev.title,
      description: description || prev.description,
      to_role: ROLE_OPTIONS.includes(toRole) ? toRole : prev.to_role,
      due_date: dueDate || prev.due_date,
    }));
  }, [searchParams]);

  useEffect(() => {
    const focusTicket = String(searchParams.get("focusTicket") || "").trim();
    if (!focusTicket || !tickets.length) return;
    const match = tickets.find((t) => t.id === focusTicket);
    if (match) {
      setDetailsModal({ open: true, ticket: match });
      (async () => {
        const events = await api.workflowTickets.events(match.id).catch(() => []);
        setTicketEvents(Array.isArray(events) ? events : []);
      })();
    }
  }, [searchParams, tickets]);

  useEffect(() => {
    setForm((prev) => {
      const next = { ...prev };
      let changed = false;
      if (next.source_type === "manual" && next.source_id) {
        next.source_id = "";
        changed = true;
      }
      if (
        !next.title.trim() &&
        next.source_id &&
        ["order", "invoice", "payment"].includes(next.source_type)
      ) {
        const prefix =
          next.source_type.charAt(0).toUpperCase() + next.source_type.slice(1);
        next.title = `${prefix} ${String(next.source_id).slice(0, 8)} follow-up`;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [form.source_type, form.source_id]);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      await showError("Ticket title is required");
      return;
    }
    setSaving(true);
    try {
      await api.workflowTickets.create({
        title: form.title.trim(),
        description: form.description.trim() || null,
        priority: form.priority,
        to_role: form.to_user_id ? null : form.to_role,
        to_user_id: form.to_user_id || null,
        source_type: form.source_type || "manual",
        source_id: form.source_id.trim() || null,
        due_date: form.due_date || null,
      });
      await showSuccess("Workflow ticket created");
      setShowCreateModal(false);
      setForm({
        title: "",
        description: "",
        priority: "medium",
        to_role: "finance",
        to_user_id: "",
        source_type: "manual",
        source_id: "",
        due_date: "",
      });
      await load();
    } catch (e2) {
      await showError(e2.message || "Failed to create ticket");
    } finally {
      setSaving(false);
    }
  };

  const onStatusChange = async (ticketId, nextStatus) => {
    try {
      await api.workflowTickets.updateStatus(ticketId, nextStatus);
      await load();
    } catch (e) {
      await showError(e.message || "Failed to update status");
    }
  };

  const onAccept = async (ticketId) => {
    try {
      setActionLoadingId(ticketId);
      await api.workflowTickets.accept(ticketId);
      await showSuccess("Ticket accepted and added to your today tasks");
      await load();
    } catch (e) {
      await showError(e.message || "Failed to accept ticket");
    } finally {
      setActionLoadingId("");
    }
  };

  const openCompleteModal = (ticket) => {
    setCompleteModal({ open: true, ticket });
    setCompletionRemark("");
  };

  const submitComplete = async () => {
    const ticketId = completeModal.ticket?.id;
    if (!ticketId) return;
    if (!completionRemark.trim()) {
      await showError("Please enter completion remark");
      return;
    }
    try {
      setActionLoadingId(ticketId);
      await api.workflowTickets.complete(ticketId, completionRemark.trim());
      await showSuccess("Ticket marked completed");
      setCompleteModal({ open: false, ticket: null });
      setCompletionRemark("");
      await load();
    } catch (e) {
      await showError(e.message || "Failed to complete ticket");
    } finally {
      setActionLoadingId("");
    }
  };

  const openDetails = async (ticket) => {
    setDetailsModal({ open: true, ticket });
    const events = await api.workflowTickets.events(ticket.id).catch(() => []);
    setTicketEvents(Array.isArray(events) ? events : []);
  };

  const submitThreadComment = async () => {
    if (!detailsModal.ticket?.id) return;
    if (!threadComment.trim()) {
      await showError("Write a comment first");
      return;
    }
    try {
      setThreadSaving(true);
      await api.workflowTickets.addComment(detailsModal.ticket.id, threadComment.trim());
      setThreadComment("");
      const events = await api.workflowTickets.events(detailsModal.ticket.id).catch(() => []);
      setTicketEvents(Array.isArray(events) ? events : []);
      await load();
    } catch (e) {
      await showError(e.message || "Failed to add comment");
    } finally {
      setThreadSaving(false);
    }
  };

  const formatStatus = (value) => String(value || "").replace("_", " ");

  const getStatusBadgeClass = (value) => {
    switch (String(value || "").toLowerCase()) {
      case "done":
        return "bg-emerald-100 text-emerald-700";
      case "in_progress":
        return "bg-blue-100 text-blue-700";
      case "blocked":
        return "bg-amber-100 text-amber-700";
      case "cancelled":
        return "bg-rose-100 text-rose-700";
      default:
        return "bg-stone-100 text-stone-700";
    }
  };

  const getPriorityBadgeClass = (value) => {
    switch (String(value || "").toLowerCase()) {
      case "urgent":
        return "bg-rose-100 text-rose-700";
      case "high":
        return "bg-orange-100 text-orange-700";
      case "medium":
        return "bg-cyan-100 text-cyan-700";
      default:
        return "bg-stone-100 text-stone-700";
    }
  };

  const filteredTickets = useMemo(() => {
    return tickets.filter((ticket) => {
      const priorityOk = !priority || String(ticket.priority || "").toLowerCase() === priority;
      const sourceTypeOk = !sourceType || String(ticket.source_type || "").toLowerCase() === sourceType;
      return priorityOk && sourceTypeOk;
    });
  }, [tickets, priority, sourceType]);

  const totalPages = Math.max(1, Math.ceil(filteredTickets.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedTickets = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredTickets.slice(start, start + pageSize);
  }, [filteredTickets, currentPage, pageSize]);

  const userRole = String(user?.role || "").toLowerCase();
  const isAdmin = role === "admin";
  const isManagement = role === "management";
  const canAcceptTicket = (ticket) =>
    !ticket?.to_user_id &&
    ticket?.to_role &&
    String(ticket.to_role).toLowerCase() === userRole &&
    ticket?.status !== "done" &&
    ticket?.status !== "cancelled";
  const isAssignedToMe = (ticket) => ticket?.to_user_id && ticket.to_user_id === user?.id;
  const canManageStatus = (ticket) => isAdmin || isAssignedToMe(ticket);

  const getPrimaryAction = (ticket) => {
    if (canAcceptTicket(ticket)) {
      return {
        label: "Accept",
        className: "bg-indigo-600 hover:bg-indigo-700",
        action: () => onAccept(ticket.id),
      };
    }
    if (!canManageStatus(ticket)) return null;
    if (ticket.status === "open") {
      return {
        label: "Start",
        className: "bg-blue-600 hover:bg-blue-700",
        action: () => onStatusChange(ticket.id, "in_progress"),
      };
    }
    if (ticket.status === "in_progress" || ticket.status === "blocked") {
      return {
        label: "Complete",
        className: "bg-emerald-600 hover:bg-emerald-700",
        action: () => openCompleteModal(ticket),
      };
    }
    return null;
  };

  useEffect(() => {
    if (!canUseAllView && view === "all") {
      setView("inbox");
    }
    if (!savedView && canUseAllView && view !== "all") {
      setView("all");
    }
  }, [canUseAllView, savedView, view]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-heading font-semibold text-secondary">Workflow Tickets</h1>
        <div className="flex gap-2 flex-wrap w-full">
          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="px-3 py-2 text-sm bg-primary text-white rounded-lg"
          >
            Issue Ticket
          </button>
          <select
            value={view}
            onChange={(e) => setView(e.target.value)}
            className="px-3 py-2 border border-stone-300 rounded-lg text-sm min-w-[140px]"
          >
            <option value="inbox">Inbox</option>
            <option value="outbox">Outbox</option>
            {canUseAllView && <option value="all">All</option>}
          </select>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="px-3 py-2 border border-stone-300 rounded-lg text-sm min-w-[120px]"
          >
            <option value="">All status</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {formatStatus(s)}
              </option>
            ))}
          </select>
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            className="px-3 py-2 border border-stone-300 rounded-lg text-sm min-w-[120px]"
          >
            <option value="">All priority</option>
            {PRIORITY_OPTIONS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <select
            value={sourceType}
            onChange={(e) => setSourceType(e.target.value)}
            className="px-3 py-2 border border-stone-300 rounded-lg text-sm min-w-[120px]"
          >
            <option value="">All sources</option>
            {SOURCE_TYPE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          {(isAdmin || isManagement) && (
            <>
              <select
                value={toRoleFilter}
                onChange={(e) => setToRoleFilter(e.target.value)}
                className="px-3 py-2 border border-stone-300 rounded-lg text-sm min-w-[130px]"
              >
                <option value="">All target roles</option>
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              <select
                value={assigneeFilter}
                onChange={(e) => setAssigneeFilter(e.target.value)}
                className="px-3 py-2 border border-stone-300 rounded-lg text-sm min-w-[140px]"
              >
                <option value="">All assignees</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="px-3 py-2 border border-stone-300 rounded-lg text-sm min-w-[140px]"
              />
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="px-3 py-2 border border-stone-300 rounded-lg text-sm min-w-[140px]"
              />
            </>
          )}
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search title/description"
            className="px-3 py-2 border border-stone-300 rounded-lg text-sm min-w-[190px]"
          />
          <div className="flex border border-stone-300 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setDisplayMode("cards")}
              className={`px-3 py-2 text-sm ${displayMode === "cards" ? "bg-stone-800 text-white" : "bg-white text-stone-700"}`}
            >
              Cards
            </button>
            <button
              type="button"
              onClick={() => setDisplayMode("table")}
              className={`px-3 py-2 text-sm ${displayMode === "table" ? "bg-stone-800 text-white" : "bg-white text-stone-700"}`}
            >
              Table
            </button>
            <button
              type="button"
              onClick={() => setDisplayMode("kanban")}
              className={`px-3 py-2 text-sm ${displayMode === "kanban" ? "bg-stone-800 text-white" : "bg-white text-stone-700"}`}
            >
              Kanban
            </button>
          </div>
          <button
            type="button"
            onClick={load}
            className="px-3 py-2 text-sm bg-primary text-white rounded-lg"
          >
            Refresh
          </button>
        </div>
      </div>
      {adminSummary && (isAdmin || isManagement) && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="bg-white border border-stone-200 rounded-xl p-3">
              <p className="text-xs text-stone-500">Overdue open</p>
              <p className="text-2xl font-semibold text-rose-700">
                {adminSummary.overdue_open || 0}
              </p>
            </div>
            <div className="bg-white border border-stone-200 rounded-xl p-3">
              <p className="text-xs text-stone-500">Average close time</p>
              <p className="text-2xl font-semibold text-stone-800">
                {adminSummary.avg_close_hours || 0}h
              </p>
            </div>
            <div className="bg-white border border-stone-200 rounded-xl p-3 lg:col-span-3">
              <p className="text-xs text-stone-500 mb-1">
                Status distribution
              </p>
              <div className="flex gap-2 flex-wrap">
                {(adminSummary.by_status || []).map((row) => (
                  <span
                    key={row.status}
                    className="px-2 py-1 rounded-full text-xs bg-stone-100 text-stone-700"
                  >
                    {formatStatus(row.status)}: {row.count}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

      {canCreate && showCreateModal && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[1px] flex items-center justify-center p-4">
          <div className="w-full max-w-3xl bg-white rounded-2xl border border-stone-200 shadow-xl max-h-[92vh] overflow-y-auto">
            <form onSubmit={onCreate} className="p-5 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h2 className="font-semibold text-stone-800 text-lg">Issue Ticket</h2>
                  <p className="text-xs text-stone-500">Create and assign a ticket in seconds</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-3 py-1.5 text-sm border border-stone-300 rounded-lg hover:bg-stone-50"
                >
                  Close
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="space-y-1">
                  <span className="text-xs font-medium text-stone-600">Ticket title *</span>
                  <input
                    value={form.title}
                    onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                    placeholder="Payment follow-up for April invoices"
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm"
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-stone-600">Priority</span>
                  <select
                    value={form.priority}
                    onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm"
                  >
                    {PRIORITY_OPTIONS.map((p) => (
                      <option key={p} value={p}>
                        {p.charAt(0).toUpperCase() + p.slice(1)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-stone-600">To role</span>
                  <select
                    value={form.to_role}
                    onChange={(e) => setForm((p) => ({ ...p, to_role: e.target.value }))}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm"
                  >
                    {ROLE_OPTIONS.map((r) => (
                      <option key={r} value={r}>
                        {r.charAt(0).toUpperCase() + r.slice(1)}
                      </option>
                    ))}
                  </select>
                </label>
                {isAdmin && (
                  <label className="space-y-1">
                    <span className="text-xs font-medium text-stone-600">Assign specific user (optional)</span>
                    <select
                      value={form.to_user_id}
                      onChange={(e) => setForm((p) => ({ ...p, to_user_id: e.target.value }))}
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm"
                    >
                      <option value="">Assign by role only</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name} ({u.role})
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <label className="space-y-1">
                  <span className="text-xs font-medium text-stone-600">Source type</span>
                  <select
                    value={form.source_type}
                    onChange={(e) => setForm((p) => ({ ...p, source_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm"
                  >
                    {SOURCE_TYPE_OPTIONS.map((sourceTypeValue) => (
                      <option key={sourceTypeValue} value={sourceTypeValue}>
                        {sourceTypeValue.charAt(0).toUpperCase() + sourceTypeValue.slice(1)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-stone-600">Source ID (optional)</span>
                  <input
                    value={form.source_id}
                    onChange={(e) => setForm((p) => ({ ...p, source_id: e.target.value }))}
                    placeholder="Order/Invoice ID"
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm"
                  />
                  <p className="text-[11px] text-stone-500">
                    Source ID auto-clears for Manual. For Order/Invoice it helps auto-title and tracking.
                  </p>
                </label>
                <label className="space-y-1 md:col-span-2">
                  <span className="text-xs font-medium text-stone-600">Due date (optional)</span>
                  <input
                    type="date"
                    value={form.due_date}
                    onChange={(e) => setForm((p) => ({ ...p, due_date: e.target.value }))}
                    className="w-full md:w-72 px-3 py-2 border border-stone-300 rounded-lg text-sm"
                  />
                </label>
              </div>

              <label className="space-y-1 block">
                <span className="text-xs font-medium text-stone-600">Description</span>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  rows={3}
                  placeholder="Add context, next step, and expected outcome..."
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm"
                />
              </label>

              <div className="flex items-center justify-end gap-3 flex-wrap">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-primary text-white rounded-lg text-sm disabled:opacity-50"
                >
                  {saving ? "Creating..." : "Create Ticket"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-4 text-sm text-stone-500">Loading tickets...</div>
        ) : tickets.length === 0 ? (
          <div className="p-4 text-sm text-stone-500">No tickets found.</div>
        ) : displayMode === "cards" ? (
          <div className="p-4 space-y-3">
            {filteredTickets.map((t) => (
              <div key={t.id} className="border border-stone-200 rounded-xl p-4 bg-white">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="space-y-1">
                    <p className="font-semibold text-stone-800">{t.title}</p>
                    {t.description && <p className="text-sm text-stone-600">{t.description}</p>}
                    <p className="text-xs text-stone-500">
                      From: {t.from_user_name || t.from_role || "-"} | To: {t.to_user_name || t.to_role || "-"}
                    </p>
                    <p className="text-xs text-stone-500">
                      Assigned by: {t.assigned_by_name || t.from_user_name || "-"} | Started by: {t.started_by_name || "-"} | Closed by: {t.completed_by_name || "-"}
                    </p>
                    {!t.to_user_id && t.to_role && (
                      <p className="text-xs text-blue-700">
                        Waiting for {t.to_role} team acceptance
                      </p>
                    )}
                    {(t.source_type || t.source_id) && (
                      <p className="text-xs text-stone-500">
                        Source: {t.source_type || "manual"} {t.source_id ? `(${t.source_id})` : ""}
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => openDetails(t)}
                      className="text-xs text-primary hover:underline"
                    >
                      Open thread
                    </button>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getPriorityBadgeClass(t.priority)}`}>
                      {String(t.priority || "medium").toUpperCase()}
                    </span>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(t.status)}`}>
                      {formatStatus(t.status)}
                    </span>
                    <span className="text-xs text-stone-500">
                      Due: {t.due_date ? String(t.due_date).slice(0, 10) : "-"}
                    </span>
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex gap-2 flex-wrap">
                    {getPrimaryAction(t) && (
                      <button
                        type="button"
                        onClick={getPrimaryAction(t).action}
                        disabled={actionLoadingId === t.id}
                        className={`px-3 py-1.5 text-xs rounded-lg text-white disabled:opacity-50 ${getPrimaryAction(t).className}`}
                      >
                        {getPrimaryAction(t).label}
                      </button>
                    )}
                    {canManageStatus(t) && t.status !== "blocked" && t.status !== "done" && t.status !== "cancelled" && (
                      <button
                        type="button"
                        onClick={() => onStatusChange(t.id, "blocked")}
                        disabled={actionLoadingId === t.id}
                        className="px-3 py-1.5 text-xs rounded-lg bg-amber-500 text-white hover:bg-amber-600"
                      >
                        Block
                      </button>
                    )}
                    {canManageStatus(t) && t.status !== "open" && (
                      <button
                        type="button"
                        onClick={() => onStatusChange(t.id, "open")}
                        disabled={actionLoadingId === t.id}
                        className="px-3 py-1.5 text-xs rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50"
                      >
                        Reopen
                      </button>
                    )}
                    {canManageStatus(t) && t.status !== "cancelled" && t.status !== "done" && (
                      <button
                        type="button"
                        onClick={() => onStatusChange(t.id, "cancelled")}
                        disabled={actionLoadingId === t.id}
                        className="px-3 py-1.5 text-xs rounded-lg border border-rose-300 text-rose-700 hover:bg-rose-50"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                  {isAdmin && (
                    <select
                      value={t.status}
                      onChange={(e) => onStatusChange(t.id, e.target.value)}
                      className="px-2.5 py-1.5 border border-stone-300 rounded-lg text-xs"
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          Move to: {formatStatus(s)}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : displayMode === "table" ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-stone-600 uppercase">Title</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-stone-600 uppercase">From</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-stone-600 uppercase">To</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-stone-600 uppercase">Priority</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-stone-600 uppercase">Status</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-stone-600 uppercase">Due</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-stone-600 uppercase">Owners</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-stone-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTickets.map((t) => (
                  <tr key={t.id} className="border-b border-stone-100">
                    <td className="py-3 px-4">
                      <p className="font-medium text-stone-800">{t.title}</p>
                      <p className="text-xs text-stone-500">
                        {t.source_type || "manual"} {t.source_id ? `(${t.source_id})` : ""}
                      </p>
                    </td>
                    <td className="py-3 px-4 text-sm text-stone-600">{t.from_user_name || t.from_role || "-"}</td>
                    <td className="py-3 px-4 text-sm text-stone-600">{t.to_user_name || t.to_role || "-"}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getPriorityBadgeClass(t.priority)}`}>
                        {String(t.priority || "medium").toUpperCase()}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(t.status)}`}>
                        {formatStatus(t.status)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-stone-600">{t.due_date ? String(t.due_date).slice(0, 10) : "-"}</td>
                    <td className="py-3 px-4 text-xs text-stone-600">
                      <div>Assigned: {t.assigned_by_name || t.from_user_name || "-"}</div>
                      <div>Started: {t.started_by_name || "-"}</div>
                      <div>Closed: {t.completed_by_name || "-"}</div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => openDetails(t)}
                          className="px-2.5 py-1 text-xs rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50"
                        >
                          Thread
                        </button>
                        {getPrimaryAction(t) && (
                          <button
                            type="button"
                            onClick={getPrimaryAction(t).action}
                            disabled={actionLoadingId === t.id}
                            className={`px-2.5 py-1 text-xs rounded-lg text-white disabled:opacity-50 ${getPrimaryAction(t).className}`}
                          >
                            {getPrimaryAction(t).label}
                          </button>
                        )}
                        {isAdmin && (
                          <select
                            value={t.status}
                            onChange={(e) => onStatusChange(t.id, e.target.value)}
                            className="px-2 py-1 border border-stone-300 rounded text-xs"
                          >
                            {STATUS_OPTIONS.map((s) => (
                              <option key={s} value={s}>
                                {formatStatus(s)}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <TablePagination
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setPage}
              totalItems={filteredTickets.length}
              pageSize={pageSize}
              onPageSizeChange={(n) => {
                setPageSize(n);
                setPage(1);
              }}
            />
          </div>
        ) : (
          <div className="p-4">
            <p className="text-xs text-stone-500 mb-3">
              Helper: Drag/drop is not enabled yet. Use ticket action buttons to move status.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
              {STATUS_OPTIONS.map((columnStatus) => (
                <div key={columnStatus} className="bg-stone-50 border border-stone-200 rounded-xl p-3">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-stone-800">{formatStatus(columnStatus)}</h3>
                    <span className="text-xs text-stone-500">
                      {filteredTickets.filter((t) => t.status === columnStatus).length}
                    </span>
                  </div>
                  <div className="space-y-2 max-h-[65vh] overflow-y-auto">
                    {filteredTickets
                      .filter((ticket) => ticket.status === columnStatus)
                      .map((ticket) => (
                        <div key={ticket.id} className="bg-white border border-stone-200 rounded-lg p-2">
                          <p className="text-sm font-medium text-stone-800">{ticket.title}</p>
                          <p className="text-xs text-stone-500 mt-1">
                            {ticket.to_user_name || ticket.to_role || "Unassigned"}
                          </p>
                          <div className="mt-2 flex items-center justify-between gap-2">
                            <button
                              type="button"
                              onClick={() => openDetails(ticket)}
                              className="text-xs text-primary hover:underline"
                            >
                              Thread
                            </button>
                            {getPrimaryAction(ticket) && (
                              <button
                                type="button"
                                onClick={getPrimaryAction(ticket).action}
                                disabled={actionLoadingId === ticket.id}
                                className={`px-2 py-1 rounded text-xs text-white disabled:opacity-50 ${getPrimaryAction(ticket).className}`}
                              >
                                {getPrimaryAction(ticket).label}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    {filteredTickets.filter((t) => t.status === columnStatus).length === 0 && (
                      <p className="text-xs text-stone-400">No tickets</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {completeModal.open && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[1px] flex items-center justify-center p-4">
          <div className="w-full max-w-lg bg-white rounded-2xl border border-stone-200 shadow-xl p-5 space-y-4">
            <h3 className="text-lg font-semibold text-stone-800">Complete Ticket</h3>
            <p className="text-sm text-stone-600">{completeModal.ticket?.title}</p>
            <label className="space-y-1 block">
              <span className="text-xs font-medium text-stone-600">Completion remark *</span>
              <textarea
                value={completionRemark}
                onChange={(e) => setCompletionRemark(e.target.value)}
                rows={4}
                placeholder="Write what was done, reference number, and outcome..."
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm"
              />
            </label>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setCompleteModal({ open: false, ticket: null })}
                className="px-3 py-2 text-sm border border-stone-300 rounded-lg hover:bg-stone-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitComplete}
                disabled={actionLoadingId === completeModal.ticket?.id}
                className="px-3 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                Mark Completed
              </button>
            </div>
          </div>
        </div>
      )}

      {detailsModal.open && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[1px] flex items-center justify-center p-4">
          <div className="w-full max-w-3xl bg-white rounded-2xl border border-stone-200 shadow-xl p-5 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-stone-800">Ticket Thread</h3>
                <p className="text-sm text-stone-600">{detailsModal.ticket?.title}</p>
                <p className="text-xs text-stone-500 mt-1">
                  Use comments for handoff updates, references, and completion notes.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDetailsModal({ open: false, ticket: null })}
                className="px-3 py-1.5 text-sm border border-stone-300 rounded-lg hover:bg-stone-50"
              >
                Close
              </button>
            </div>

            <div className="space-y-2 border border-stone-200 rounded-xl p-3 max-h-80 overflow-y-auto">
              {ticketEvents.length === 0 ? (
                <p className="text-sm text-stone-500">No thread updates yet.</p>
              ) : (
                ticketEvents.map((event) => (
                  <div key={event.id} className="border-b border-stone-100 pb-2 last:border-0">
                    <p className="text-xs text-stone-500">
                      {event.actor_name || event.actor_role || "System"} - {new Date(event.created_at).toLocaleString()}
                    </p>
                    <p className="text-sm text-stone-800">
                      {event.event_type.replace(/_/g, " ")}
                      {event.old_status || event.new_status
                        ? ` (${event.old_status || "-"} -> ${event.new_status || "-"})`
                        : ""}
                    </p>
                    {event.comment && <p className="text-sm text-stone-600 mt-0.5">{event.comment}</p>}
                  </div>
                ))
              )}
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-stone-600 block">
                Add thread comment
              </label>
              <textarea
                value={threadComment}
                onChange={(e) => setThreadComment(e.target.value)}
                rows={3}
                placeholder="Write update, reference number, blockers, or handoff note..."
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm"
              />
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={submitThreadComment}
                  disabled={threadSaving}
                  className="px-3 py-2 text-sm bg-primary text-white rounded-lg disabled:opacity-50"
                >
                  {threadSaving ? "Posting..." : "Post comment"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

